import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import './BagngoStats.css';

const BagngoStats = () => {
  const token = localStorage.getItem('token');

  // Filters State
  const [localisation, setLocalisation] = useState("All");
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  // Data States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [bills, setBills] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Helper function to extract location/product ID robustly
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    return loc.toString();
  };

  // Fetch all required data
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError("");
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const configAuth = { headers: { Authorised: token } };

      const [locationsRes, productsRes, billsRes, invoicesRes] = await Promise.all([
        axios.get("/api/Locations/getAllLocations"),
        axios.get("/api/ProductV/getProductV?slim=true", configAuth),
        axios.get(`/api/BillsValises/getAllBillsV?slim=true&startDate=${startDate}&endDate=${endDate}`, configAuth),
        axios.get("/api/InvoicesV/getInvoices?slim=true")
      ]);

      setLocations(locationsRes.data || []);
      setProducts(productsRes.data || []);
      setBills(billsRes.data || []);
      setInvoices(invoicesRes.data || []);
    } catch (err) {
      console.error("Error fetching Bagn'Go stats data:", err);
      setError("Une erreur est survenue lors du chargement des données. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 1. Identify Bagn'Go Locations
  const bngLocations = useMemo(() => {
    return locations.filter(loc => {
      const nameLower = (loc.name || "").toLowerCase();
      return nameLower.includes("bagngo") || nameLower.includes("bng");
    });
  }, [locations]);

  // Set of Bagn'Go Location IDs
  const bngLocationIds = useMemo(() => {
    return new Set(bngLocations.map(loc => extractLocId(loc._id)));
  }, [bngLocations]);

  // Map of Location ID to Location Name
  const bngLocationsMap = useMemo(() => {
    const map = new Map();
    bngLocations.forEach(loc => {
      const id = extractLocId(loc._id);
      if (id) map.set(id, loc.name);
    });
    return map;
  }, [bngLocations]);

  // 2. Pre-calculate Product Categories & Parents (App 1 / Valises)
  const productMap = useMemo(() => {
    const map = new Map();
    products.forEach(p => {
      map.set(`prod_${p._id}`, { ...p, isSub: false });
      if (p.subProductsV && Array.isArray(p.subProductsV)) {
        p.subProductsV.forEach(sub => {
          map.set(`sub_${sub._id}`, { 
            ...sub, 
            parentName: p.name, 
            parentId: p._id, 
            parentCategory: p.category, 
            isSub: true 
          });
        });
      }
    });
    return map;
  }, [products]);

  // 3. Pre-calculate Purchase Prices (TTC) from purchase invoices
  const purchasePriceMap = useMemo(() => {
    const map = new Map();
    invoices.forEach(inv => {
      if (!inv.items || !Array.isArray(inv.items)) return;
      inv.items.forEach(item => {
        let purchasePriceTTC = 0;
        if (item.pricettc && item.pricettc > 0) {
          purchasePriceTTC = item.pricettc;
        } else {
          const purchasePriceHT = item.price || 0;
          const tvaRate = item.tva || 0;
          purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
        }

        const itemProductId = item.productId?.toString() || item.productV?.toString() || item.productV?._id?.toString();
        const itemSubProductId = item.subProductId?.toString() || item.subProductV?.toString() || item.subProductV?._id?.toString();

        if (itemSubProductId) {
          map.set(`sub_${itemSubProductId}`, purchasePriceTTC);
        }
        if (itemProductId && !map.has(`prod_${itemProductId}`)) {
          map.set(`prod_${itemProductId}`, purchasePriceTTC);
        }
      });
    });
    return map;
  }, [invoices]);

  const findPurchasePrice = useCallback((productId, subProductId) => {
    if (subProductId && purchasePriceMap.has(`sub_${subProductId}`)) {
      return purchasePriceMap.get(`sub_${subProductId}`);
    }
    if (productId && purchasePriceMap.has(`prod_${productId}`)) {
      return purchasePriceMap.get(`prod_${productId}`);
    }
    return 0;
  }, [purchasePriceMap]);

  // 4. Filter Sales Bills for Bagn'Go
  const filteredSalesBills = useMemo(() => {
    return bills.filter(bill => {
      if (bill.status === "cancelled") return false;

      // Extract location ID
      const locId = extractLocId(bill.localisation);

      // Check if this is a Bagn'Go bill (either by location ID, location name, or user role)
      let isBngBill = false;
      if (locId && bngLocationIds.has(locId)) {
        isBngBill = true;
      } else if (bill.user) {
        const role = (bill.user.role || "").toLowerCase();
        const name = (bill.user.name || "").toLowerCase();
        if (role === "userbagngo" || name.includes("bagngo") || name.includes("bng")) {
          isBngBill = true;
        }
      }

      // Check Location filter
      if (localisation !== "All") {
        return isBngBill && locId === localisation;
      }

      return isBngBill;
    });
  }, [bills, bngLocationIds, localisation]);

  // 5. Filter Purchase Invoices for Bagn'Go
  const filteredPurchaseInvoices = useMemo(() => {
    const start = startDate ? new Date(startDate).getTime() : null;
    const end = endDate ? new Date(endDate).getTime() : null;

    return invoices.filter(inv => {
      const locId = extractLocId(inv.localisation);
      if (!locId) return false;

      const isBngLoc = localisation === "All" ? bngLocationIds.has(locId) : locId === localisation;
      if (!isBngLoc) return false;

      const invDate = new Date(inv.date).getTime();
      return (!start || invDate >= start) && (!end || invDate <= end);
    });
  }, [invoices, bngLocationIds, localisation, startDate, endDate]);

  // 6. Aggregate Sales Metrics, Categories & Best Sales Days
  const processedMetrics = useMemo(() => {
    let totalCA = 0;
    let totalCost = 0;
    let totalItemsSold = 0;
    const dailySalesMap = {};
    const categoryMap = {
      VALISES: { quantity: 0, sales: 0, cost: 0 },
      SACS: { quantity: 0, sales: 0, cost: 0 },
      ACCESSOIRES: { quantity: 0, sales: 0, cost: 0 },
      SET: { quantity: 0, sales: 0, cost: 0 },
      Autre: { quantity: 0, sales: 0, cost: 0 }
    };
    const productSalesMap = {};
    let bestSaleAmount = 0;
    let bestSaleDetails = null;

    filteredSalesBills.forEach(bill => {
      const dateKey = moment(bill.date || bill.createdAt).format("YYYY-MM-DD");
      let billSalesAmount = 0;
      let billCostAmount = 0;

      bill.items?.forEach(item => {
        if (item.isCancelled) return;
        const quantity = item.quantity || 1;
        const sellingPrice = item.price || 0;
        const salesAmount = sellingPrice * quantity;

        // Resolve Product details
        const itemId = item.id?.toString() || item._id?.toString();
        const prodMatch = productMap.get(`prod_${itemId}`);
        const subMatch = productMap.get(`sub_${itemId}`);

        let category = "Autre";
        let purchasePrice = 0;
        let pName = item.name || "Produit inconnu";

        if (prodMatch && !prodMatch.isSub) {
          category = prodMatch.category || "Autre";
          pName = prodMatch.name;
          if (prodMatch.category === "SET" && prodMatch.subProductsV?.length > 0) {
            purchasePrice = prodMatch.subProductsV.reduce((acc, sub) => acc + findPurchasePrice(prodMatch._id, sub._id), 0);
          } else {
            purchasePrice = findPurchasePrice(prodMatch._id, null);
          }
        } else if (subMatch && subMatch.isSub) {
          category = subMatch.parentCategory || "Autre";
          pName = `${subMatch.parentName} - ${subMatch.name}`;
          purchasePrice = findPurchasePrice(subMatch.parentId, subMatch._id);
        }

        const costAmount = purchasePrice * quantity;
        totalCA += salesAmount;
        totalCost += costAmount;
        totalItemsSold += quantity;
        billSalesAmount += salesAmount;
        billCostAmount += costAmount;

        // Map Category stats
        const catKey = categoryMap[category] ? category : "Autre";
        categoryMap[catKey].quantity += quantity;
        categoryMap[catKey].sales += salesAmount;
        categoryMap[catKey].cost += costAmount;

        // Map Product sales stats
        if (!productSalesMap[itemId]) {
          productSalesMap[itemId] = { name: pName, category, quantity: 0, sales: 0, cost: 0 };
        }
        productSalesMap[itemId].quantity += quantity;
        productSalesMap[itemId].sales += salesAmount;
        productSalesMap[itemId].cost += costAmount;
      });

      // Daily trend
      dailySalesMap[dateKey] = (dailySalesMap[dateKey] || 0) + billSalesAmount;

      // Track single highest bill sale
      if (billSalesAmount > bestSaleAmount) {
        bestSaleAmount = billSalesAmount;
        bestSaleDetails = {
          billNumber: bill.billNumber || "N/A",
          total: billSalesAmount,
          date: bill.date || bill.createdAt,
          itemsCount: bill.items?.filter(i => !i.isCancelled).length || 0
        };
      }
    });

    // Determine Best Sales Day
    let bestDayDate = "N/A";
    let bestDayAmount = 0;
    Object.entries(dailySalesMap).forEach(([date, amt]) => {
      if (amt > bestDayAmount) {
        bestDayAmount = amt;
        bestDayDate = date;
      }
    });

    // Determine Top Products
    const topProducts = Object.values(productSalesMap)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    return {
      totalCA,
      totalCost,
      totalMargin: totalCA - totalCost,
      marginPercentage: totalCA > 0 ? ((totalCA - totalCost) / totalCA) * 100 : 0,
      totalItemsSold,
      averageBasket: filteredSalesBills.length > 0 ? totalCA / filteredSalesBills.length : 0,
      dailySalesMap,
      categoryMap,
      bestSaleDetails,
      bestDay: { date: bestDayDate, amount: bestDayAmount },
      topProducts,
      productSalesMap
    };
  }, [filteredSalesBills, productMap, findPurchasePrice]);

  const categoryRankings = useMemo(() => {
    const categoriesList = ["VALISES", "SACS", "ACCESSOIRES", "SET"];
    const rankings = {};
    
    categoriesList.forEach(cat => {
      rankings[cat] = Object.values(processedMetrics.productSalesMap || {})
        .filter(p => p.category === cat)
        .sort((a, b) => b.quantity - a.quantity);
    });
    
    return rankings;
  }, [processedMetrics.productSalesMap]);

  // 7. Aggregate Purchases Rhythm (Invoices breakdown)
  const purchasesMetrics = useMemo(() => {
    let totalPurchasedCost = 0;
    let totalPurchasedItems = 0;
    const purchasesList = [];

    filteredPurchaseInvoices.forEach(inv => {
      let invQty = 0;
      const categoriesSet = new Set();

      inv.items?.forEach(item => {
        invQty += (item.quantity || 0);
        const itemId = item.productId?.toString() || item.productV?.toString() || item.productV?._id?.toString();
        const prodMatch = productMap.get(`prod_${itemId}`);
        if (prodMatch) {
          categoriesSet.add(prodMatch.category || "Autre");
        } else {
          categoriesSet.add("Autre");
        }
      });

      totalPurchasedCost += (inv.totalAmount || 0);
      totalPurchasedItems += invQty;

      purchasesList.push({
        id: inv._id,
        date: inv.date,
        supplierName: inv.supplierV?.name || "Fournisseur inconnu",
        locationName: bngLocationsMap.get(extractLocId(inv.localisation)) || "Localisation inconnue",
        categories: Array.from(categoriesSet).join(", "),
        quantity: invQty,
        totalAmount: inv.totalAmount || 0
      });
    });

    // Sort purchases by date desc
    purchasesList.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      totalPurchasedCost,
      totalPurchasedItems,
      purchasesList
    };
  }, [filteredPurchaseInvoices, productMap, bngLocationsMap]);

  // 8. Sales Cadence & Trend Chart Data
  const salesCadenceChartData = useMemo(() => {
    const list = [];
    const start = moment(startDate);
    const end = moment(endDate);

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
      const dateStr = m.format("YYYY-MM-DD");
      list.push({
        dateLabel: m.format("DD/MM"),
        sales: processedMetrics.dailySalesMap[dateStr] || 0
      });
    }
    return list;
  }, [startDate, endDate, processedMetrics.dailySalesMap]);

  // 9. Categories Chart Data (PieChart)
  const categoriesPieChartData = useMemo(() => {
    const data = [];
    let idCounter = 0;
    const colors = ['#f97316', '#ee5d50', '#ff9800', '#2196F3', '#4CAF50'];

    Object.entries(processedMetrics.categoryMap).forEach(([cat, stats]) => {
      if (stats.sales > 0) {
        const pct = processedMetrics.totalCA > 0 ? ((stats.sales / processedMetrics.totalCA) * 100).toFixed(1) + "%" : "0%";
        data.push({
          id: idCounter++,
          value: stats.sales,
          label: `${cat} (${pct})`,
          color: colors[idCounter % colors.length]
        });
      }
    });

    return data;
  }, [processedMetrics.categoryMap, processedMetrics.totalCA]);

  // Formatting utilities
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  };

  return (
    <div className="bagngo-dashboard-container">
      <div className="bagngo-header">
        <h1>Statistiques Bagn'Go</h1>
        <p>Suivi analytique des ventes, des marges par produit et du rythme d'approvisionnement</p>
      </div>

      {/* Filtres */}
      <div className="bagngo-filters">
        <div className="filter-group">
          <label>Boutique / Localisation</label>
          <select value={localisation} onChange={(e) => setLocalisation(e.target.value)}>
            <option value="All">Toutes les boutiques Bagn'Go</option>
            {bngLocations.map(loc => (
              <option key={loc._id} value={extractLocId(loc._id)}>{loc.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Date de début</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Date de fin</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#f97316', fontWeight: 'bold' }}>Chargement des données Bagn'Go en cours...</p>}
      {error && <p style={{ textAlign: 'center', color: '#F44336', fontWeight: 'bold' }}>{error}</p>}

      {/* KPIs Section */}
      <div className="bagngo-kpis">
        <div className="kpi-card">
          <div className="kpi-title">CA Réel (POS)</div>
          <div className="kpi-value primary">{formatCurrency(processedMetrics.totalCA)}</div>
          <small style={{ color: '#666' }}>Articles vendus: {processedMetrics.totalItemsSold}</small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Marge brute</div>
          <div className={`kpi-value ${processedMetrics.totalMargin >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(processedMetrics.totalMargin)}
          </div>
          <small style={{ fontWeight: 'bold', color: processedMetrics.totalMargin >= 0 ? '#4CAF50' : '#F44336' }}>
            Taux: {processedMetrics.marginPercentage.toFixed(1)}%
          </small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Panier moyen</div>
          <div className="kpi-value info">{formatCurrency(processedMetrics.averageBasket)}</div>
          <small style={{ color: '#666' }}>Nombre de tickets: {filteredSalesBills.length}</small>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Meilleur jour de vente</div>
          <div className="kpi-value warning" style={{ fontSize: '1.6rem', padding: '6px 0' }}>
            {processedMetrics.bestDay.date !== "N/A" ? moment(processedMetrics.bestDay.date).format("DD MMM YYYY") : "N/A"}
          </div>
          <small style={{ fontWeight: 'bold', color: '#ff9800' }}>
            Volume: {formatCurrency(processedMetrics.bestDay.amount)}
          </small>
        </div>
      </div>

      {/* Graphiques Section */}
      <div className="bagngo-charts">
        <div className="chart-container" style={{ flex: 2 }}>
          <div className="chart-title">📈 Cadence de Vente (CA quotidien)</div>
          {salesCadenceChartData.length > 0 ? (
            <LineChart
              dataset={salesCadenceChartData}
              xAxis={[{ scaleType: 'point', dataKey: 'dateLabel' }]}
              series={[{ dataKey: 'sales', color: '#f97316', label: 'Ventes (TND)' }]}
              width={750}
              height={260}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#888' }}>Aucune donnée de vente pour cette période.</p>
          )}
        </div>

        <div className="chart-container">
          <div className="chart-title">🍕 Ventes par Catégorie</div>
          {categoriesPieChartData.length > 0 ? (
            <PieChart
              series={[{
                data: categoriesPieChartData,
                innerRadius: 35,
                outerRadius: 85,
                paddingAngle: 4,
                cornerRadius: 4,
              }]}
              width={380}
              height={240}
              slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' } } }}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#888' }}>Aucune vente enregistrée.</p>
          )}
        </div>
      </div>

      {/* Tableau statistiques par catégorie */}
      <div className="bagngo-table-container">
        <h3 style={{ marginBottom: '20px', color: '#333' }}>📦 Statistiques par type de produit (Catégories)</h3>
        <div className="table-responsive">
          <table className="bagngo-table">
            <thead>
              <tr>
                <th>Type de Produit (Catégorie)</th>
                <th>Quantité vendue</th>
                <th>CA Réel (Ventes)</th>
                <th>Coût d'Achat Réel</th>
                <th style={{ color: '#f97316' }}>Marge Brute</th>
                <th>Taux de Marge</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(processedMetrics.categoryMap).map(([cat, stats]) => {
                const margin = stats.sales - stats.cost;
                const marginPct = stats.sales > 0 ? (margin / stats.sales) * 100 : 0;
                return (
                  <tr key={cat}>
                    <td style={{ fontWeight: 'bold' }}>{cat}</td>
                    <td style={{ fontWeight: '600', color: '#374151' }}>{stats.quantity}</td>
                    <td style={{ fontWeight: '600', color: '#111827' }}>{formatCurrency(stats.sales)}</td>
                    <td style={{ color: '#c2410c', fontWeight: '600' }}>{formatCurrency(stats.cost)}</td>
                    <td style={{ fontWeight: 'bold', color: margin >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(margin)}</td>
                    <td style={{ fontWeight: 'bold', color: '#b45309' }}>{marginPct.toFixed(1)}%</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid rgba(249, 115, 22, 0.3)', fontWeight: 'bold' }}>
                <td>TOTAL CUMULÉ</td>
                <td style={{ color: '#111827' }}>{processedMetrics.totalItemsSold}</td>
                <td style={{ color: '#111827' }}>{formatCurrency(processedMetrics.totalCA)}</td>
                <td style={{ color: '#c2410c' }}>{formatCurrency(processedMetrics.totalCost)}</td>
                <td style={{ color: processedMetrics.totalMargin >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(processedMetrics.totalMargin)}</td>
                <td style={{ color: '#b45309' }}>{processedMetrics.marginPercentage.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section des classements par catégorie */}
      <div className="category-rankings-section">
        <h3 className="section-title">🏆 Classement des Ventes par Catégorie (Valise, Sac, Accessoires, SET)</h3>
        <div className="category-rankings-grid">
          {["VALISES", "SACS", "ACCESSOIRES", "SET"].map(cat => {
            const catIcon = cat === "VALISES" ? "🧳" : cat === "SACS" ? "🎒" : cat === "ACCESSOIRES" ? "🧣" : "📦";
            const rankings = categoryRankings[cat] || [];
            return (
              <div key={cat} className="bagngo-table-container ranking-card">
                <h4 className="ranking-card-title">
                  <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>{catIcon}</span>
                  Catégorie : {cat}
                </h4>
                <div className="table-responsive ranking-table-wrapper">
                  <table className="bagngo-table compact">
                    <thead>
                      <tr>
                        <th>Rang</th>
                        <th>Produit</th>
                        <th style={{ textAlign: 'center' }}>Qté</th>
                        <th>CA Réel</th>
                        <th>Coût d'Achat</th>
                        <th>Marge</th>
                        <th>% Marge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings.map((p, index) => {
                        const margin = p.sales - p.cost;
                        const marginPct = p.sales > 0 ? (margin / p.sales) * 100 : 0;
                        return (
                          <tr key={index}>
                            <td style={{ fontWeight: 'bold', color: '#ea580c' }}>#{index + 1}</td>
                            <td style={{ fontWeight: '600' }} title={p.name}>
                              {p.name}
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: '600', color: '#374151' }}>{p.quantity}</td>
                            <td style={{ fontWeight: '600', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                            <td style={{ color: '#c2410c', fontWeight: '600' }}>{formatCurrency(p.cost)}</td>
                            <td style={{ fontWeight: 'bold', color: margin >= 0 ? '#15803d' : '#b91c1c' }}>
                              {formatCurrency(margin)}
                            </td>
                            <td style={{ fontWeight: 'bold', color: '#b45309' }}>
                              {marginPct.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                      {rankings.length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', color: '#888', padding: '40px 10px', fontStyle: 'italic' }}>
                            Aucune vente pour la catégorie {cat} sur cette période.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Purchase Rhythm */}
      <div className="bagngo-table-container">
        <h3 style={{ marginBottom: '20px', color: '#333' }}>🚚 Rythme d'Achat (Entrées de Stock Récentes)</h3>
        <div className="table-responsive">
          <table className="bagngo-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Fournisseur</th>
                <th>Boutique</th>
                <th>Qté Achetée</th>
                <th>Montant total</th>
              </tr>
            </thead>
            <tbody>
              {purchasesMetrics.purchasesList.map((p, index) => (
                <tr key={p.id || index}>
                  <td>{moment(p.date).format("DD/MM/YYYY")}</td>
                  <td style={{ fontWeight: '600' }}>{p.supplierName}</td>
                  <td>
                    <span className="badge-secondary">{p.locationName.replace("Bagn'Go ", "").replace("Bagn'go ", "")}</span>
                  </td>
                  <td>{p.quantity} pcs</td>
                  <td style={{ fontWeight: 'bold', color: '#ee5d50' }}>{formatCurrency(p.totalAmount)}</td>
                </tr>
              ))}
              {purchasesMetrics.purchasesList.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
                    Aucune facture d'achat trouvée pour cette période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default BagngoStats;
